import {
  get as getProp,
  set as setProp
} from 'dot-prop';
import _ from 'lodash';
import {
  DesignChangeMap,
  DesignChangeMapModule
} from '../../../../types';
import {
  deleteProp,
  ValueOf
} from '../../../functions';
import {
  ConflictResolution,
  DesignConflictItem
} from '../types';
import { getChangeItemObject } from './get-change-item-object';

type ModuleChange = ValueOf<Required<DesignChangeMapModule>>;

/** Remove change map properties that are empty after resolving conflicts. */
function removeUnusedProps(
  changeMap: DesignChangeMap,
  parentKey: string
): void {
  if (!parentKey) {
    return;
  }

  const parentChange = getProp(changeMap, parentKey);
  const isParentEmpty = (parentChange instanceof Array) ? (
    parentChange.length < 1
  ) : Object.keys(parentChange).length < 1;
  if (!isParentEmpty) {
    return;
  }

  deleteProp(changeMap, parentKey);
  const splitKey = parentKey.split('.');
  removeUnusedProps(
    changeMap,
    splitKey.slice(0, splitKey.length - 1).join('.')
  );
}

/**
 * Resolve conflicts by removing changes from @see changeMap for conflicts
 * with their resolution set to @see ConflictResolution.target.
 */
export function resolveDesignConflictItems(
  changeMap: DesignChangeMap,
  conflicts: DesignConflictItem[]
): DesignChangeMap {
  changeMap = _.cloneDeep(changeMap);
  conflicts.filter((item) => {
    // We will remove source changes if the user selects target.
    return item.resolution === ConflictResolution.target;
  }).forEach((item) => {
    const splitKey = item.key.split('.');
    const baseKey = splitKey[0] as keyof(DesignChangeMap);
    const childKey = splitKey[splitKey.length - 1];
    const parentKey = splitKey.slice(0, splitKey.length - 1).join('.');

    let foundChangeItem = false;
    let parentChange = (parentKey) ? (
      getProp(changeMap, parentKey)
    ) : changeMap;
    if (baseKey === 'modules') {
      parentChange = (parentChange as (ModuleChange[0])[]).filter((change) => {
        const object = getChangeItemObject(change);
        if (object.id !== childKey) {
          return true;
        }

        foundChangeItem = true;
        return false;
      });
      setProp(changeMap, parentKey, parentChange);
    } else if (parentChange[childKey]) {
      foundChangeItem = true;
      delete parentChange[childKey];
    }

    removeUnusedProps(changeMap, parentKey);

    if (!foundChangeItem) {
      console.error(changeMap);
      throw new Error(`Unable to get '${parentKey}.${childKey}' in change map.`);
    }
  });

  return changeMap;
}
